/**
 * Validates that coordinates are within valid ranges
 * @param lat Latitude value
 * @param lng Longitude value
 * @returns true if coordinates are valid, false otherwise
 */
export function isValidCoordinate(lat: number | null, lng: number | null): boolean {
  return (
    typeof lat === 'number' && 
    typeof lng === 'number' &&
    !isNaN(lat) && 
    !isNaN(lng) &&
    lat >= -90 && 
    lat <= 90 && 
    lng >= -180 && 
    lng <= 180
  );
}

/**
 * Creates points for a circle approximation
 * @param center The center point of the circle [longitude, latitude]
 * @param radiusInMeters The radius of the circle in meters
 * @returns An array of points that approximate a circle
 */
export function createCirclePoints(center: [number, number], radiusInMeters: number): [number, number][] {

  const points: [number, number][] = [];
  const steps = 64; // Number of points to approximate the circle
  
  // Earth's radius in meters
  const earthRadius = 6378137;
  
  // Convert radius from meters to radians
  const radiusInRadians = radiusInMeters / earthRadius;
  
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    
    // Calculate point using spherical law of cosines
    const lat = Math.asin(
      Math.sin(center[1] * Math.PI / 180) * Math.cos(radiusInRadians) +
      Math.cos(center[1] * Math.PI / 180) * Math.sin(radiusInRadians) * Math.cos(angle)
    ) * 180 / Math.PI;
    
    const lng = center[0] + Math.atan2(
      Math.sin(angle) * Math.sin(radiusInRadians) * Math.cos(center[1] * Math.PI / 180),
      Math.cos(radiusInRadians) - Math.sin(center[1] * Math.PI / 180) * Math.sin(lat * Math.PI / 180)
    ) * 180 / Math.PI;
    
    points.push([lng, lat]);
  }
  
  // Close the polygon by repeating the first point
  points.push([...points[0]]);
  
  return points;
}

/**
 * Convert a metre-radius circle to the polygon ring expected by the backend.
 * Keeping this conversion next to the spherical point calculation prevents
 * callers from reintroducing degree-based radius approximations.
 */
export function circleGeometryToPolygon(
  center: [number, number],
  radiusInMeters: number,
): [number, number][][] {
  return [createCirclePoints(center, radiusInMeters)];
}

/**
 * Calculate distance between two points in degrees (approximate)
 * @param point1 The first point [longitude, latitude]
 * @param point2 The second point [longitude, latitude]
 * @returns The distance in degrees
 */
export function calculateDistance(point1: [number, number], point2: [number, number]): number {
  const [lng1, lat1] = point1;
  const [lng2, lat2] = point2;
  
  // Simple Euclidean distance (good enough for small areas)
  return Math.sqrt(
    Math.pow(lng2 - lng1, 2) + 
    Math.pow(lat2 - lat1, 2)
  );
}

/**
 * Calculate the Haversine distance between two points in meters
 * @param point1 The first point [longitude, latitude]
 * @param point2 The second point [longitude, latitude]
 * @returns The distance in meters
 */
export function calculateHaversineDistance(point1: [number, number], point2: [number, number]): number {
  const [lng1, lat1] = point1;
  const [lng2, lat2] = point2;
  
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
           Math.cos(φ1) * Math.cos(φ2) *
           Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c;
}

/**
 * Calculate bounding box for a polygon or multipolygon
 * @param polygon Coordinates array from GeoJSON Polygon or MultiPolygon
 * @returns [minLng, minLat, maxLng, maxLat] or null if invalid
 */
export function calculateBounds(polygon: number[][][] | number[][][][]): [number, number, number, number] | null {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let hasPoints = false;

  // Helper to process a single ring (array of points)
  const processRing = (ring: number[][]) => {
    for (const point of ring) {
      if (point.length >= 2) {
        const [lng, lat] = point;
        minLng = Math.min(minLng, lng);
        minLat = Math.min(minLat, lat);
        maxLng = Math.max(maxLng, lng);
        maxLat = Math.max(maxLat, lat);
        hasPoints = true;
      }
    }
  };

  // Helper to process a polygon (array of rings)
  const processPolygon = (poly: number[][][]) => {
    for (const ring of poly) {
      processRing(ring);
    }
  };

  if (!Array.isArray(polygon) || polygon.length === 0) return null;

  // Detect if MultiPolygon (4 levels deep) or Polygon (3 levels deep)
  // A point is number[], a ring is number[][], a polygon is number[][][]
  const isMultiPolygon = Array.isArray(polygon[0]) && Array.isArray(polygon[0][0]) && Array.isArray(polygon[0][0][0]);

  if (isMultiPolygon) {
    for (const poly of polygon as number[][][][]) {
      processPolygon(poly);
    }
  } else {
    // Treat as single Polygon (array of rings)
    // Check if it's actually a Polygon structure (array of rings)
    if (Array.isArray(polygon[0]) && Array.isArray(polygon[0][0])) {
       processPolygon(polygon as number[][][]);
    }
  }

  if (!hasPoints) return null;

  return [minLng, minLat, maxLng, maxLat];
}
